import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  MapPin, 
  Edit, 
  Trash2, 
  Eye,
  Globe,
  Building,
  Users,
  RefreshCw,
  AlertCircle,
  Filter,
  MoreHorizontal,
  Calendar,
  Activity,
  ShoppingBag,
  Store,
  Target,
  X
} from 'lucide-react';
import ApiService from '../services/api';

interface City {
  id: number;
  name_ar: string;
  name_en?: string;
  country: string;
  region?: string;
  is_active: boolean;
  branches_count?: number;
  active_offers?: number;
  merchants_count?: number;
  population?: number;
  created_at: string;
  updated_at: string;
}

interface CityStats {
  total: number;
  active: number;
  inactive: number;
  totalOffers: number;
  totalMerchants: number;
  totalBranches: number;
}

const CitiesManagement: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [deletingCityId, setDeletingCityId] = useState<number | null>(null);
  const [stats, setStats] = useState<CityStats | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 تحميل بيانات المدن...');
      
      // تحميل المدن والعروض والمتاجر للحصول على الإحصائيات
      const [citiesData, offersData, merchantsData] = await Promise.all([
        ApiService.getCities(),
        ApiService.getAllOffers().catch(() => []),
        ApiService.getMerchants().catch(() => [])
      ]);

      console.log('🏙️ Cities loaded:', citiesData);

      const cities = citiesData || [];
      setCities(cities);
      
      // حساب الإحصائيات
      const offers = offersData || [];
      const merchants = merchantsData?.merchants || merchantsData || [];
      
      const citiesWithStats = cities.map((city: City) => {
        const cityOffers = offers.filter((offer: any) => offer.city_id === city.id);
        const cityMerchants = new Set(cityOffers.map((offer: any) => offer.merchant_id)).size;
        
        return {
          ...city,
          branches_count: Math.floor(Math.random() * 50) + 5, // مؤقت
          active_offers: cityOffers.length,
          merchants_count: cityMerchants,
          population: getRandomPopulation(city.name_ar) // مؤقت
        };
      });
      
      setCities(citiesWithStats);
      
      // حساب الإحصائيات العامة
      setStats({
        total: citiesWithStats.length,
        active: citiesWithStats.filter((c: City) => c.is_active).length,
        inactive: citiesWithStats.filter((c: City) => !c.is_active).length,
        totalOffers: offers.length,
        totalMerchants: merchants.length,
        totalBranches: citiesWithStats.reduce((sum: number, c: City) => sum + (c.branches_count || 0), 0)
      });
      
    } catch (err: any) {
      console.error('❌ Error loading cities:', err);
      setError('فشل في تحميل بيانات المدن. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const getRandomPopulation = (cityName: string) => {
    // محاكاة بيانات السكان بناءً على اسم المدينة
    const populations: { [key: string]: number } = {
      'الرياض': 7000000,
      'جدة': 4000000,
      'مكة': 2000000,
      'المدينة': 1300000,
      'الدمام': 1500000,
      'الطائف': 700000
    };
    
    return populations[cityName] || Math.floor(Math.random() * 2000000) + 500000;
  };

  const handleRefresh = () => {
    loadCities();
  };

  // إضافة مدينة جديدة
  const handleCreateCity = async (formData: FormData) => {
    try {
      const nameEnValue = formData.get('name_en') as string;
      
      const cityData: any = {
        name_ar: formData.get('name_ar'),
        country: formData.get('country'),
      };
      
      // Only include optional fields if they have values
      if (nameEnValue && nameEnValue.trim()) {
        cityData.name_en = nameEnValue;
      }

      await ApiService.createCity(cityData);
      setShowAddModal(false);
      loadCities();
    } catch (error: any) {
      console.error('Error creating city:', error);
      const errorMessage = error.message || 'فشل في إضافة المدينة';
      alert(`خطأ: ${errorMessage}`);
    }
  };

  // تعديل مدينة
  const handleEditCity = async (formData: FormData) => {
    try {
      const nameEnValue = formData.get('name_en') as string;
      
      const cityData: any = {
        name_ar: formData.get('name_ar'),
        country: formData.get('country'),
        is_active: formData.get('is_active') === 'true',
      };
      
      // Only include optional fields if they have values
      if (nameEnValue && nameEnValue.trim()) {
        cityData.name_en = nameEnValue;
      }

      console.log('Updating city with data:', cityData);
      await ApiService.updateCity(editingCity!.id, cityData);
      setEditingCity(null);
      loadCities();
    } catch (error: any) {
      console.error('Error updating city:', error);
      const errorMessage = error.message || 'فشل في تحديث المدينة';
      alert(`خطأ: ${errorMessage}`);
    }
  };

  // حذف مدينة
  const handleDeleteCity = async (cityId: number) => {
    try {
      await ApiService.deleteCity(cityId);
      setDeletingCityId(null);
      setCities(cities.filter(city => city.id !== cityId));
    } catch (error) {
      console.error('Error deleting city:', error);
      alert('فشل في حذف المدينة');
    }
  };

  // فلترة المدن
  const filteredCities = cities.filter(city => {
    const matchesSearch = city.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         city.name_en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && city.is_active) ||
                         (statusFilter === 'inactive' && !city.is_active);
    
    return matchesSearch && matchesStatus;
  });

  // التصفح بالصفحات
  const totalPages = Math.ceil(filteredCities.length / itemsPerPage);
  const currentCities = filteredCities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">جاري تحميل المدن...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={handleRefresh} className="btn-primary">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-responsive-xl font-bold gradient-text">
            إدارة المدن والمناطق
          </h1>
          <p className="text-gray-600 mt-2">
            إدارة معلومات المدن والمناطق الجغرافية المدعومة في النظام
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} className="btn-outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            تحديث
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary group"
          >
            <Plus className="w-4 h-4 ml-2 group-hover:rotate-90 transition-transform duration-300" />
            إضافة مدينة جديدة
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="metrics-grid">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-primary">
                  <MapPin className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-primary-600">
                  المجموع
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.total}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">إجمالي المدن</p>
                <p className="text-xs text-gray-600">المسجلة في النظام</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-success">
                  <Activity className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-success-600">
                  نشطة
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.active}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">مدن نشطة</p>
                <p className="text-xs text-gray-600">تحتوي على متاجر</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-secondary">
                  <Building className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-secondary-600">
                  الفروع
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.totalBranches}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">إجمالي الفروع</p>
                <p className="text-xs text-gray-600">في جميع المدن</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-warning">
                  <Store className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-warning-600">
                  المتاجر
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.totalMerchants}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">إجمالي المتاجر</p>
                <p className="text-xs text-gray-600">المسجلة في النظام</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-gradient"
      >
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث في المدن..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pr-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {filteredCities.length} من {cities.length} مدينة
              </span>
            </div>
            
            <div></div>
          </div>
        </div>
      </motion.div>

      {/* Cities Grid */}
      {currentCities.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentCities.map((city, index) => (
            <motion.div
              key={city.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card-gradient group hover-lift"
            >
              <div className="card-header">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-glow">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                        {city.name_ar}
                      </h3>
                      {city.name_en && (
                        <p className="text-sm text-gray-600">{city.name_en}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${city.is_active ? 'badge-success' : 'badge-error'}`}>
                      {city.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                    <button className="text-gray-400 hover:text-gray-600 transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {/* Country and Population */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 p-2 bg-white/60 rounded-lg">
                    <Globe className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm text-gray-700">{city.country}</span>
                  </div>
                  
                  {city.population && (
                    <div className="flex items-center gap-2 p-2 bg-white/60 rounded-lg">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-700">{formatNumber(city.population)} نسمة</span>
                    </div>
                  )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
                      <Building className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{city.branches_count || 0}</div>
                    <div className="text-xs text-gray-600">فرع</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-secondary-600 mb-1">
                      <ShoppingBag className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{city.active_offers || 0}</div>
                    <div className="text-xs text-gray-600">عرض نشط</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-warning-600 mb-1">
                      <Store className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{city.merchants_count || 0}</div>
                    <div className="text-xs text-gray-600">متجر</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
                  <div className="flex items-center gap-2">
                    <button className="btn btn-sm btn-outline hover:bg-primary-50 hover:text-primary-700">
                      <Eye className="w-4 h-4 ml-1" />
                      عرض
                    </button>
                    
                    <button 
                      onClick={() => setEditingCity(city)}
                      className="btn btn-sm btn-outline hover:bg-secondary-50 hover:text-secondary-700"
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      تعديل
                    </button>
                    
                    <button 
                      onClick={() => setDeletingCityId(city.id)}
                      className="btn btn-sm btn-outline hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 ml-1" />
                      حذف
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(city.created_at)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">لا توجد مدن</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all'
              ? 'لا توجد مدن تطابق معايير البحث'
              : 'لم يتم تسجيل أي مدن بعد'
            }
          </p>
          {(!searchTerm && statusFilter === 'all') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 ml-2" />
              إضافة أول مدينة
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center gap-2"
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-outline"
          >
            السابق
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  currentPage === page
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-primary-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-outline"
          >
            التالي
          </button>
        </motion.div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal max-w-2xl"
          >
            <div className="p-6 border-b border-gray-200/50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">إضافة مدينة جديدة</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleCreateCity(formData);
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      اسم المدينة (عربي) *
                    </label>
                    <input 
                      type="text" 
                      name="name_ar"
                      className="input" 
                      placeholder="مثال: الرياض" 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      اسم المدينة (إنجليزي)
                    </label>
                    <input 
                      type="text" 
                      name="name_en"
                      className="input" 
                      placeholder="Example: Riyadh" 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    الدولة *
                  </label>
                  <select name="country" className="input" required>
                    <option value="السعودية">السعودية</option>
                    <option value="الإمارات">الإمارات</option>
                    <option value="الكويت">الكويت</option>
                    <option value="قطر">قطر</option>
                    <option value="البحرين">البحرين</option>
                    <option value="عمان">عمان</option>
                  </select>
                </div>
                
                <div className="p-6 border-t border-gray-200/50 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-outline"
                  >
                    إلغاء
                  </button>
                  <button type="submit" className="btn-primary">
                    إضافة المدينة
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit City Modal */}
      {editingCity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">تعديل المدينة</h3>
              <button
                onClick={() => setEditingCity(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleEditCity(formData);
              }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    اسم المدينة (عربي) *
                  </label>
                  <input 
                    type="text" 
                    name="name_ar"
                    defaultValue={editingCity.name_ar}
                    className="input" 
                    placeholder="مثال: الرياض" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    اسم المدينة (إنجليزي)
                  </label>
                  <input 
                    type="text" 
                    name="name_en"
                    defaultValue={editingCity.name_en}
                    className="input" 
                    placeholder="Example: Riyadh" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  الدولة *
                </label>
                <select 
                  name="country" 
                  defaultValue={editingCity.country}
                  className="input" 
                  required
                >
                  <option value="السعودية">السعودية</option>
                  <option value="الإمارات">الإمارات</option>
                  <option value="الكويت">الكويت</option>
                  <option value="قطر">قطر</option>
                  <option value="البحرين">البحرين</option>
                  <option value="عمان">عمان</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  حالة المدينة
                </label>
                <select 
                  name="is_active"
                  defaultValue={editingCity.is_active.toString()}
                  className="input"
                >
                  <option value="true">نشطة</option>
                  <option value="false">غير نشطة</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  حفظ التغييرات
                </button>
                <button
                  type="button"
                  onClick={() => setEditingCity(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingCityId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                حذف المدينة
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                هل أنت متأكد من حذف هذه المدينة؟ سيتم حذف جميع العروض والفروع المرتبطة بها أيضاً. لا يمكن التراجع عن هذا الإجراء.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteCity(deletingCityId)}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  حذف
                </button>
                <button
                  onClick={() => setDeletingCityId(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CitiesManagement;